import React from 'react'
import { motion } from 'framer-motion'
import { useTheme } from '@/hooks/useTheme'

export interface AverageDiscountChartData {
  filledPercentage?: number // Percentage of left segment to fill (0-100), default: ~66%
}

interface AverageDiscountChartProps {
  data?: AverageDiscountChartData
  className?: string
}

const AverageDiscountChart: React.FC<AverageDiscountChartProps> = ({ 
  className = ''
}) => {
  const { theme } = useTheme()
  // According to Figma metadata: black line is at x=106 (which is 106px from left, not centered)
  // Total width is 214px, so center would be 107px, but line is at 106px (1px offset)
  // Line position: left-[106px] (106px from left edge of 214px container)

  return (
    <div className={`relative w-full max-w-[214px] h-[80px] overflow-hidden ${className}`}>
      {/* Left segment - Yellow-green gradient (93px width, 35px height, top: 35px) - reduced by 20% */}
      <motion.div
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: '93px', opacity: 1 }}
        transition={{
          duration: 0.6,
          delay: 0,
          ease: [0.16, 1, 0.3, 1]
        }}
        className="absolute left-0 top-[35px] w-[93px] h-[35px] overflow-hidden"
        style={{ 
          background: 'linear-gradient(to right, rgba(244,254,192,0.24) 0%, #e3fd5a 100%)',
        }}
      />
      
      {/* Gray block - 13px width, 35px height, top: 35px, left: 93px - reduced by 20% */}
      <motion.div
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: '13px', opacity: 1 }}
        transition={{
          duration: 0.4,
          delay: 0.2,
          ease: [0.16, 1, 0.3, 1]
        }}
        className="absolute left-[93px] top-[35px] w-[13px] h-[35px] bg-neutral-200 dark:bg-[#bfbcbc]"
      />
      
      {/* Divider - black vertical line (2px width, 61px height, top: 10px, left: 106px) - reduced by 20% */}
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: '61px', opacity: 1 }}
        transition={{
          duration: 0.5,
          delay: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }}
        className="absolute left-[106px] top-[10px] w-[2px] h-[61px] bg-[#2d2923] dark:bg-[#fdfdfd]"
      />
      
      {/* Right segment - Dashed lines (should start after black line, from 108px to 214px = 106px width) */}
      <motion.div
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: '106px', opacity: 1 }}
        transition={{
          duration: 0.6,
          delay: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }}
        className="absolute left-[108px] top-[54px] w-[106px] h-4"
        style={{ 
          background: `repeating-linear-gradient(90deg, transparent, transparent 2px, ${theme === 'dark' ? '#606060' : 'rgba(45,41,35,0.32)'} 2px, ${theme === 'dark' ? '#606060' : 'rgba(45,41,35,0.32)'} 4px)`
        }}
      />
    </div>
  )
}

export default AverageDiscountChart

